import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage, Messaging } from 'firebase/messaging';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FcmNotificationService {
  private notificationsSubject = new BehaviorSubject<any[]>([]);
  notifications$ = this.notificationsSubject.asObservable();

  private messageSubject = new BehaviorSubject<any>(null);
  message$ = this.messageSubject.asObservable();

  private messagingPromise: Promise<Messaging | null>;
  private hasLoadedInitialNotifications = false;
  private isListening = false;
  constructor(
    private http: HttpClient,
    private zone: NgZone
  ) {
    this.messagingPromise = this.initMessaging();
  }
  private loadInitialNotifications(): void {
    if (this.hasLoadedInitialNotifications) return;
    this.hasLoadedInitialNotifications = true;

    this.getMyNotifications().subscribe({
      next: (items) => {
        const mapped = (items || [])
          .map(entry => this.mapBackendNotification(entry))
          .filter(Boolean);
        this.setApiNotifications(mapped);
      },
      error: (error) => {
        console.error('Initial notifications load failed', error);
      }
    });
  }
  private async initMessaging(): Promise<Messaging | null> {
    try {
      const supported = await isSupported();
      if (!supported) return null;
      const app = getApps().length ? getApps()[0] : initializeApp(environment.firebase);
      return getMessaging(app);
    } catch (error) {
      console.error('FCM not supported', error);
      return null;
    }
  }
  // ==================== API NOTIFICATIONS (REPLACE - NO MERGE) ====================
  getMyNotifications(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.api_url}/api/Notification/MyNotifications`);
  }

  /** Clear current list and set new data from API */
  setApiNotifications(notifications: any[]): void {
    this.notificationsSubject.next(notifications || []);   // ← Just replace, no merge
  }
  // NEW METHOD - Call this instead of directly calling getMyNotifications in component
  loadNotifications(): void {


    this.getMyNotifications().subscribe({
      next: (items) => {
        const mapped = (items || [])
          .map(entry => this.mapBackendNotification(entry))  // Move mapping here if you want
          .filter(Boolean);
        this.setApiNotifications(mapped);
      },
      error: (error) => {
        console.error('Notifications load failed', error);
      }
    });
  }

  private mapBackendNotification(entry: any) {
    if (!entry) {
      return null;
    }
    const backendId = entry?.id ?? entry?.Id;
    const createdAtValue = entry?.createdAt ?? entry?.CreatedAt ?? entry?.date ?? entry?.Date;
    let createdAt: number | undefined;
    if (createdAtValue) {
      const parsed = new Date(createdAtValue).getTime();
      if (!Number.isNaN(parsed)) {
        createdAt = parsed;
      }
    }
    return {
      id: backendId ?? `backend-${Date.now()}`,
      backendId: backendId,
      title: entry?.title ?? entry?.Title ?? 'Notification',
      message: entry?.message ?? entry?.Message ?? entry?.body ?? entry?.Body ?? entry?.content ?? entry?.Content ?? '',
      time: createdAt ? undefined : entry?.time ?? entry?.Time,
      createdAt: createdAt,
      icon: entry?.icon ?? entry?.Icon ?? 'uil-bell',
      read: entry?.isRead ?? entry?.IsRead ?? false,
      link: entry?.link ?? entry?.Link ?? 'javascript:void(0);'
    };
  }
  // ==================== FCM NEW NOTIFICATION (MERGE ONLY) ====================
  prependNotification(notification: any): void {
    if (!notification) return;

    console.log("🚀 ~ FcmNotificationService ~ prependNotification :", notification);

    const current = [...this.notificationsSubject.value];
    const newId = notification.backendId ?? notification.id;

    if (!newId) {
      console.warn("Notification without backendId");
      return;
    }

    // STRICT duplicate check
    const alreadyExists = current.some(n =>
      (n.backendId ?? n.id) === newId
    );

    if (alreadyExists) {
      console.log('Duplicate blocked by ID:', newId);
      return;
    }

    console.log('Adding new notification with ID:', newId);
    this.notificationsSubject.next([notification, ...current]);
  }
  private getNotificationKey(notification: any): string | number | null {
    if (!notification) return null;
    return notification.backendId ?? notification.id ?? null;
  }

  // ==================== OTHER METHODS ====================
  getNotificationsSnapshot(): any[] {
    return this.notificationsSubject.value;
  }

  async requestPermission(): Promise<void> {
    try {
      if (!environment.vapidKey) {
        console.warn('FCM VAPID key is missing.');
        return;
      }

      const messaging = await this.messagingPromise;
      if (!messaging) return;

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission denied');
        return;
      }

      const serviceWorkerRegistration = await this.getServiceWorkerRegistration(); // keep your existing method

      const token = await getToken(messaging, {
        vapidKey: environment.vapidKey,
        serviceWorkerRegistration: serviceWorkerRegistration ?? undefined
      });

      if (token) {
        this.saveToken(token);
      }
    } catch (error) {
      console.error('FCM permission error', error);
    }
  }
  private async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service Worker not supported');
      return null;
    }

    try {
      const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
      let resolvedBase = baseHref;

      if (resolvedBase === '/' && window.location.pathname && window.location.pathname !== '/') {
        const segments = window.location.pathname.split('/').filter(Boolean);
        if (segments.length > 0) {
          resolvedBase = `/${segments[0]}/`;
        }
      }

      const normalizedBase = resolvedBase.endsWith('/') ? resolvedBase : `${resolvedBase}/`;
      const swUrl = `${normalizedBase}firebase-messaging-sw.js`;

      return await navigator.serviceWorker.register(swUrl);
    } catch (error) {
      console.error('Service Worker registration failed', error);
      return null;
    }
  }
  async listen(): Promise<void> {
    if (this.isListening) return;   // Add this flag at class level
    this.isListening = true;

    const messaging = await this.messagingPromise;
    if (!messaging) {
      console.warn('FCM Messaging not available');
      return;
    }

    console.log('FCM: Starting to listen for messages...');
    
    onMessage(messaging, (payload) => {
      console.log("FCM Raw Payload Received:", payload);
      
      // Show browser notification for foreground messages
      this.showForegroundNotification(payload);
      
      this.zone.run(() => {
        this.messageSubject.next(payload);
      });
    });
  }

  private showForegroundNotification(payload: any): void {
    try {
      const notification = payload?.notification || {};
      const data = payload?.data || {};
      
      const title = notification.title || data.title || 'Notification';
      const body = notification.body || data.body || data.message || '';
      
      if (title || body) {
        // Create browser notification
        const browserNotification = new Notification(title, {
          body: body,
          icon: '/assets/favicon.ico',
          badge: '/assets/favicon.ico',
          tag: data.notificationId || 'default',
          data: data,
          requireInteraction: false
        });
        
        // Auto-close after 5 seconds
        setTimeout(() => {
          browserNotification.close();
        }, 5000);
        
        // Handle click
        browserNotification.onclick = (event) => {
          event.preventDefault();
          browserNotification.close();
          
          if (data.link && data.link !== 'javascript:void(0);') {
            window.open(data.link, '_blank');
          }
        };
        
        console.log('Foreground notification displayed:', { title, body });
      }
    } catch (error) {
      console.error('Error showing foreground notification:', error);
    }
  }

  markNotificationsRead(ids: Array<string | number>): void {
    if (!ids.length) return;
    this.http.post(`${environment.api_url}/api/Notification/MarkReadBulk`, {
      notificationIds: ids,
      isRead: true
    }).subscribe({ error: err => console.error(err) });
  }

  unregisterToken(token: string): Observable<any> {
    return this.http.post(`${environment.api_url}/api/Notification/UnregisterToken`, { token });
  }

  private saveToken(token: string): void {
    console.log('Saving FCM token:', token.substring(0, 20) + '...');
    localStorage.setItem('fcmToken', token);
    
    this.http.post(`${environment.api_url}/api/Notification/RegisterToken`, { token })
      .subscribe({
        next: (response) => {
          console.log('FCM token registered successfully:', response);
        },
        error: (error) => {
          console.error('Failed to register FCM token:', error);
        }
      });
  }
  reset(): void {
  this.hasLoadedInitialNotifications = false;
  this.isListening = false;
  this.notificationsSubject.next([]);
  this.messageSubject.next(null);
}

  // Debug method to check FCM status
  async debugFcmStatus(): Promise<void> {
    console.log('=== FCM Debug Info ===');
    
    // Check Firebase support
    const supported = await isSupported();
    console.log('FCM Supported:', supported);
    
    // Check notification permission
    console.log('Notification Permission:', Notification.permission);
    
    // Check token
    const token = localStorage.getItem('fcmToken');
    console.log('Stored Token:', token ? `${token.substring(0, 20)}...` : 'None');
    
    // Check service worker
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      console.log('Service Workers:', registrations.length);
      registrations.forEach(reg => {
        console.log('- SW Scope:', reg.scope);
        console.log('- SW State:', reg.active?.state);
      });
    }
    
    // Check messaging instance
    const messaging = await this.messagingPromise;
    console.log('Messaging Instance:', !!messaging);
    
    console.log('Environment:', {
      api_url: environment.api_url,
      hasVapidKey: !!environment.vapidKey,
      hasFirebase: !!environment.firebase
    });
    
    console.log('=== End FCM Debug ===');
  }
}
